import { 
    Button, CircularProgress, Dialog, DialogActions, DialogContent, 
    DialogTitle, TextField, makeStyles, Grid,
    Snackbar
} from "@material-ui/core";
import { useState } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { customtheme } from "../../../../../app/theme/theme";
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles(() => ({
    dialogContent: {
      minWidth: 600,
    },
    backgroundColor: {
      background: customtheme.blue[500],
      color: customtheme.white,
      '&:hover': {
        background: customtheme.blue[50],
        color: customtheme.blue[500],
      },
    },
    backBackgroundColor: {
      background: customtheme.gray[200],
      color: customtheme.blue[500],
      marginRight: 20,
      '&:hover': {
        background: customtheme.blue[50],
        color: customtheme.blue[500],
      },
    },
}));

interface CreateFornecedorProps {
    codEmp: string;
    codFil: string;
    openCreateFornecedor: boolean;
    handleCloseDialog: () => void;
    handleUpdateField: (data: any) => void;
}

const postFornecedor = async (fornecedor: any) => {
    const token = localStorage.getItem('token')
    const response = await axios.post(process.env.REACT_APP_BASE_URL+"/erp/xml/create-fornecedor", fornecedor, {
        headers: {
            Authorization: 'Bearer ' + token
        }
    }); 
    return response.data; 
};

export const CreateFornecedor = (props: CreateFornecedorProps) => {
    const classes = useStyles();
    const { openCreateFornecedor, handleCloseDialog, handleUpdateField, codEmp, codFil } = props;

    const [isSnackbar, setIsSnackbar] = useState({open:false, message: 'info', title: ''});
    const defaultValues = {
        cnpj: "",
        razaoSocial: "",
        nomeFantasia: "",
        logradouro: "",
        numero: "",
        bairro: "",
        cidade: "",
        uf: "",
        cep: "",
        telefone: "",
    }
    const [form, setForm] = useState(defaultValues);

    const [loadingCnpj, setLoadingCnpj] = useState(false);
    const [cnpjError, setCnpjError] = useState("");

    const { mutate, isLoading: isSaving } = useMutation({
        mutationFn: postFornecedor,
        onSuccess: (data) => {
            if (!data.isError) {
                handleUpdateField({ codfor: data.data.codFor, nomfor: data.data.nomFor });
                setForm(defaultValues)
                setIsSnackbar({
                    open: true,
                    message: `Sucesso ao criar fornecedor`,
                    title: 'success'
                })
            } else {
                setIsSnackbar({
                open: true,
                message: `Erro ao criar fornecedor: ${data.message}`,
                title: 'error'
                })
            }
        },
        onError: (e) => {
            setIsSnackbar({
            open: true,
            message: `Erro ao criar fornecedor: ${e}`,
            title: 'error'
            })
        }
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCnpjBlur = async () => {
        const cnpj = form.cnpj.replace(/\D/g, "");
        if (!cnpj) return;

        const validaCnpj = /^[0-9]{14}$/;
        if (!validaCnpj.test(cnpj)) {
            setCnpjError("CNPJ inválido");
            return;
        }

        try {
            setLoadingCnpj(true);
            setCnpjError("");
            const response = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);

            if (response.status === 200) {
                const data = response.data;
                setForm((prev) => ({
                    ...prev,
                    uf: data.uf?.toUpperCase() || "",
                    bairro: data.bairro?.toUpperCase() || "",
                    cidade: data.municipio?.toUpperCase() || "",
                    logradouro: data.logradouro?.toUpperCase() || "",
                    cep: data.cep || "",
                    numero: data.numero || "",
                    razaoSocial: data.razao_social?.toUpperCase() || "",
                    nomeFantasia: data.nome_fantasia?.toUpperCase() || "",
                    telefone: data.ddd_telefone_1 || "",
                }));
            }
        } catch {
            setCnpjError("Não foi possível buscar o CNPJ");
        } finally {
            setLoadingCnpj(false);
        }
    };

    const handleSubmit = () => {
        if (!form.cnpj) {
            setCnpjError("Informe o CNPJ");
            return;
        }

        const cnpj = form.cnpj.replace(/\D/g, "");
        if(!isSaving) {
            mutate({
                codEmp: codEmp,
                codFil: codFil,
                nomFor: form.razaoSocial,
                sifUfs: form.uf,
                baiFor: form.bairro,
                cepFor: form.cep,
                cgcFor: form.cnpj,
                cidFor: form.cidade,
                endFor: form.logradouro,
                apeFor: form.nomeFantasia || form.razaoSocial.slice(0, 50),
                fonFor: form.telefone,
                nenFor: form.numero,
                tipFor: cnpj.length === 14 ? 'J' : 'P',
            });
        }
    };
    
    const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
        return;
        }

        setIsSnackbar({open: false, message: '', title: 'info'});
    };

    return (
        <>
            <Dialog open={openCreateFornecedor} onClose={() => {
                setForm(defaultValues);
                handleCloseDialog()
            }} maxWidth="md" fullWidth>
                <DialogTitle>Adicionar Fornecedor</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Grid container spacing={2}>
                        {/* CNPJ + Telefone */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="CNPJ"
                                name="cnpj"
                                value={form.cnpj}
                                onChange={handleChange}
                                onBlur={handleCnpjBlur}
                                fullWidth
                                error={!!cnpjError}
                                helperText={cnpjError}
                                InputProps={{
                                    endAdornment: loadingCnpj && <CircularProgress size={20} />,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Telefone"
                                name="telefone"
                                value={form.telefone}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                        {/* Razão Social */}
                        <Grid item xs={12}>
                            <TextField
                                label="Razão Social"
                                name="razaoSocial"
                                value={form.razaoSocial}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                        {/* Nome Fantasia */}
                        <Grid item xs={12}>
                            <TextField
                                label="Nome Fantasia"
                                name="nomeFantasia"
                                value={form.nomeFantasia}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                        {/* Logradouro + Número */}
                        <Grid item xs={12} md={9}>
                            <TextField
                                label="Logradouro"
                                name="logradouro"
                                value={form.logradouro}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Número"
                                name="numero"
                                value={form.numero}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                        {/* Bairro + Cidade */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Bairro"
                                name="bairro"
                                value={form.bairro}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Cidade"
                                name="cidade"
                                value={form.cidade}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                        {/* UF + CEP */}
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="UF"
                                name="uf"
                                value={form.uf}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <TextField
                                label="CEP"
                                name="cep"
                                value={form.cep}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setForm(defaultValues);
                            handleCloseDialog();
                        }} 
                        className={classes.backBackgroundColor}
                        disabled={isSaving}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSubmit}
                        className={classes.backgroundColor} 
                        variant="contained"
                        disabled={isSaving}
                    >
                        {isSaving ? <CircularProgress size={20} color="inherit" /> : "Confirmar"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={isSnackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={isSnackbar.title as any}>
                  {isSnackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};